'use client';

import { forwardRef } from 'react';
import { createPolymorphicComponent, Tabs as MantineTabs } from '@mantine/core';
import type {
  TabsListProps as MantineTabsListProps,
  TabsPanelProps as MantineTabsPanelProps,
  TabsProps as MantineTabsProps,
  TabsTabProps as MantineTabProps,
} from '@mantine/core';

import type { LockedPolymorphic, LockedProps } from '../types';

type TabsProps = Omit<MantineTabsProps, LockedProps>;

/**
 * A `wizard` step's status, passed as `mod={{ status }}` on its `TabsTab`, whose children are the
 * label then the status line, in that order: the theme colours the marker and the last child by it.
 */
export type TabsWizardStatus = 'complete' | 'editing' | 'incomplete';

/**
 * ### Tabs
 *
 * @description
 * Mantine's Tabs minus per-call-site style overrides (see {@link LockedProps}). Style with the
 * built-in `variant` (`default` / `outline` / `pills`), the theme's own `wizard` — a step row styled
 * by each step's {@link TabsWizardStatus} — and `folder` — a row along the bottom of a shaded header
 * whose current tab takes the page fill, with no underline — plus `color`, `radius`, `orientation`,
 * `placement` and `inverted` — there are no per-slot escape hatches. The structural guards the
 * design system needs (horizontal rows stay single-line and constrained for Scroller-backed
 * overflow, except the `wizard` row, which wraps; vertical lists stay single-column) live in the
 * theme's `Tabs` entry, so they apply to every instance. Wrap overflowing horizontal `TabsTab`s in
 * {@link Scroller} inside `TabsList` (see Mantine's Tabs + Scroller pattern).
 *
 * For a tab bar that needs a bespoke skin, add a theme-level variant rather than re-styling here.
 *
 * @example
 * ```tsx
 * <Tabs value={tab} onChange={setTab}>
 *   <TabsList>
 *     <TabsTab value="overview">Overview</TabsTab>
 *     <TabsTab value="results">Results</TabsTab>
 *   </TabsList>
 *   <TabsPanel value="overview" pt="md">…</TabsPanel>
 * </Tabs>
 * ```
 */
export const Tabs = (props: TabsProps) => {
  return <MantineTabs {...props} />;
};

type TabsListProps = Omit<MantineTabsListProps, LockedProps>;

export const TabsList = forwardRef<HTMLDivElement, TabsListProps>((props, ref) => {
  return <MantineTabs.List {...props} ref={ref} />;
});

TabsList.displayName = 'TabsList';

type TabProps = Omit<MantineTabProps, LockedProps>;

const _TabsTab = forwardRef<HTMLButtonElement, TabProps>((props, ref) => {
  return <MantineTabs.Tab {...props} ref={ref} />;
});

_TabsTab.displayName = 'TabsTab';

/**
 * ### TabsTab
 *
 * @description
 * A single tab. `className`/`style` are locked even though the component is polymorphic: the
 * intrinsic element props would otherwise re-widen them back in.
 */
export const TabsTab = createPolymorphicComponent<'button', TabProps>(
  _TabsTab,
) as LockedPolymorphic<'button', TabProps>;

type TabsPanelProps = Omit<MantineTabsPanelProps, LockedProps>;

export const TabsPanel = (props: TabsPanelProps) => {
  return <MantineTabs.Panel {...props} />;
};
